const cartDrawer = document.querySelector("cart-drawer");
let activeElement = null;

/* ============================================================================
 * TWC CART ENHANCEMENTS
 * ----------------------------------------------------------------------------
 * Added features:
 *   1. Variant selector inside the side cart (handleCartVariantChange).
 *   2. Per-order max-quantity enforcement + checkout blocking
 *      (enforceCartQuantityLimits, reads window.TWC_MAX_QTY).
 *   3. PDP add-to-cart guard (handled inside handleProductFormSubmit).
 *   4. Universal add-to-cart spinner (setupAtcSpinner / clearAtcSpinners).
 *   5. Lightweight toast for blocking messages (showAtcToast).
 * ==========================================================================*/

// ---- 4. Universal add-to-cart spinner --------------------------------------
// Any add-to-cart style button gets a spinner the moment it's clicked, so the
// customer can't fire it twice. Cleared when the drawer opens (add finished)
// or after a safety timeout.
const TWC_ATC_SELECTORS = [
  ".group-product__form-wrapper .pdm-btn",
  ".pdm-sticky-atc__atc-button",
  ".sticky-add-to-cart",
  ".fbt-form [type='submit']",
  ".form-product [type='submit']",
].join(",");

function twcAtcButtonIsDisabled(btn) {
  return (
    btn.disabled ||
    btn.getAttribute("aria-disabled") === "true" ||
    btn.classList.contains("sold-out") ||
    btn.classList.contains("is-disabled") ||
    btn.classList.contains("is-sold-out") ||
    btn.dataset.forceOos === "true"
  );
}

function clearAtcSpinners() {
  document
    .querySelectorAll(".twc-atc-loading")
    .forEach((b) => b.classList.remove("twc-atc-loading"));
}

function setupAtcSpinner() {
  // Capture phase so we mark the button before any proxy-click handler runs.
  document.addEventListener(
    "click",
    (e) => {
      const btn = e.target.closest(TWC_ATC_SELECTORS);
      if (!btn) return;
      if (twcAtcButtonIsDisabled(btn)) return;
      if (btn.classList.contains("twc-atc-loading")) return;
      btn.classList.add("twc-atc-loading");
      // Never leave a spinner stuck if something goes wrong.
      setTimeout(() => btn.classList.remove("twc-atc-loading"), 8000);
    },
    true
  );
}

// ---- 5. Toast (used for the PDP "limit reached" message) -------------------
function showAtcToast(message) {
  let toast = document.getElementById("twc-atc-toast");
  if (!toast) {
    toast = document.createElement("div");
    toast.id = "twc-atc-toast";
    toast.className = "twc-atc-toast";
    toast.setAttribute("role", "alert");
    document.body.appendChild(toast);
  }
  toast.textContent = message;
  toast.classList.add("twc-atc-toast--visible");
  clearTimeout(toast._twcTimer);
  toast._twcTimer = setTimeout(
    () => toast.classList.remove("twc-atc-toast--visible"),
    4000
  );
}

// ---- 3b. Persistent inline PDP error (shown under the ATC button) ----------
// Unlike the toast, this message stays in place so the customer understands the
// limit isn't a bug — they simply can't add more. It's re-evaluated on every
// add attempt (cleared at the top of handleProductFormSubmit, re-added if the
// product is still at its cap).
function clearPdpInlineError(form) {
  const scope = form || document;
  scope.querySelectorAll(".twc-pdp-error").forEach((el) => el.remove());
}

function showPdpInlineError(form, button, message) {
  clearPdpInlineError(form);

  const msg = document.createElement("div");
  msg.className = "twc-pdp-error";
  msg.setAttribute("role", "alert");
  msg.innerHTML =
    '<svg class="twc-pdp-error__icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
    '<circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.4"/>' +
    '<path d="M8 4.4v4.4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>' +
    '<circle cx="8" cy="11.4" r="0.9" fill="currentColor"/></svg>' +
    '<span class="twc-pdp-error__text"></span>';
  msg.querySelector(".twc-pdp-error__text").textContent = message;

  // Anchor the message directly under the button the customer clicked. When a
  // sticky bar proxy-clicks the main form, submitter is the main .pdm-btn, so
  // the message still lands under the visible on-page add-to-cart button.
  let anchor = null;
  if (button && typeof button.closest === "function") {
    anchor = button.closest(".group-product__form-wrapper") || button;
  }
  if (anchor && anchor.parentNode) {
    anchor.insertAdjacentElement("afterend", msg);
  } else if (form) {
    form.appendChild(msg);
  } else {
    showAtcToast(message); // last-resort fallback so the message is never lost
    return;
  }
  msg.scrollIntoView({ block: "nearest", behavior: "smooth" });
}

// ---- 2. Per-order max-quantity enforcement --------------------------------
// Sums the quantity of each product across ALL of its line items in the drawer
// (so variant A + variant B, or one-time + subscription of the same product,
// all count together) and compares against window.TWC_MAX_QTY[productId].
// When any product is over its limit: highlights the offending lines, shows an
// error message, and disables the checkout button until the cart is fixed.
// Hard block on checkout whenever a limit error is actually visible in the cart.
// Registered once, capture-phase, so it fires before any theme handler and can't
// be bypassed. It keys off the live DOM (a visible error) rather than a stored
// flag, so it stays correct even if a re-render or the A/B class flips between
// enforcement runs. Registered unconditionally — in the control cart there is
// never an error, so it never blocks there.
var twcCheckoutGuardReady = false;
function twcGuardCheckout() {
  if (twcCheckoutGuardReady) return;
  twcCheckoutGuardReady = true;
  document.addEventListener(
    "click",
    function (e) {
      var btn = e.target.closest && e.target.closest("#CartDrawer-Checkout");
      if (!btn) return;
      var drawer = document.querySelector("cart-drawer");
      var err =
        drawer &&
        drawer.querySelector(".cart-maxqty-error, .cart-item--limit-exceeded");
      if (btn.getAttribute("data-blocked") === "true" || err) {
        e.preventDefault();
        e.stopPropagation();
        if (err) err.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    },
    true
  );
}

function enforceCartQuantityLimits() {
  const drawer = document.querySelector("cart-drawer");
  if (!drawer) return;

  const checkoutBtn = drawer.querySelector("#CartDrawer-Checkout");

  // Clear previous messages / highlights before recalculating.
  drawer.querySelectorAll(".cart-maxqty-error").forEach((el) => el.remove());
  drawer
    .querySelectorAll(".cart-item--limit-exceeded")
    .forEach((el) => el.classList.remove("cart-item--limit-exceeded"));

  // Group line items by product (free-gift lines carry no data-product-id and
  // use the cart-item--free_gift class, so they're excluded).
  const lines = Array.from(
    drawer.querySelectorAll(".cart-item[data-product-id]:not(.cart-item--free_gift)")
  );

  // Limits come straight from each line's data-attributes, which the drawer
  // re-renders on every change. (The window.TWC_* maps are only built once at
  // page load — and from a <script> that sits OUTSIDE <cart-drawer>, so it never
  // re-runs on a drawer refresh — which is why the error needed a page reload.)
  const productLimits = {}; // pid -> cap
  const variantLimits = {}; // vid -> cap
  const exclusive = {};     // pid -> true

  const products = {}; // pid -> { total, nodes, variants: { vid: {qty, title, nodes} } }
  lines.forEach((el) => {
    const pid = el.getAttribute("data-product-id");
    if (!pid) return;
    const vid = el.getAttribute("data-variant-id") || "0";
    const qty = parseInt(el.getAttribute("data-quantity"), 10) || 0;
    const vtitle = el.getAttribute("data-variant-title") || "";

    const pCap = parseInt(el.getAttribute("data-max-quantity"), 10);
    if (pCap > 0) productLimits[pid] = pCap;
    const vCap = parseInt(el.getAttribute("data-variant-max-quantity"), 10);
    if (vCap > 0) variantLimits[vid] = vCap;
    if (el.getAttribute("data-variant-exclusive") === "true") exclusive[pid] = true;

    const p = (products[pid] = products[pid] || { total: 0, nodes: [], variants: {} });
    p.total += qty;
    p.nodes.push(el);
    const v = (p.variants[vid] = p.variants[vid] || { qty: 0, title: vtitle, nodes: [] });
    v.qty += qty;
    v.nodes.push(el);
  });

  let hasViolation = false;

  const buildMsg = (html) => {
    const msg = document.createElement("div");
    msg.className = "cart-maxqty-error";
    msg.setAttribute("role", "alert");
    msg.innerHTML =
      '<span class="cart-maxqty-error__icon" aria-hidden="true">!</span>' +
      '<span class="cart-maxqty-error__text">' + html + "</span>";
    return msg;
  };

  Object.keys(products).forEach((pid) => {
    const p = products[pid];
    const firstNode = p.nodes[0];
    const titleEl = firstNode.querySelector(".cart-item__name");
    const productTitle = titleEl ? titleEl.textContent.trim() : "This item";
    const variantIds = Object.keys(p.variants);
    const messages = []; // { html, nodes }

    // ---- Layer 1: variant exclusivity (only one variant allowed at a time) --
    if (exclusive[pid] && variantIds.length > 1) {
      const names = variantIds
        .map((vid) => p.variants[vid].title)
        .filter(Boolean)
        .join(" and ");
      messages.push({
        html:
          "You can only have one option of <b>" +
          productTitle +
          "</b> in your cart at a time" +
          (names ? " (" + names + ")" : "") +
          ". Please remove one to continue.",
        nodes: p.nodes,
      });
    } else {
      // ---- Layer 2: per-variant caps ----------------------------------------
      let variantViolation = false;
      variantIds.forEach((vid) => {
        const vLimit = parseInt(variantLimits[vid], 10);
        const v = p.variants[vid];
        if (vLimit && vLimit > 0 && v.qty > vLimit) {
          variantViolation = true;
          const excess = v.qty - vLimit;
          const label = v.title ? productTitle + " – " + v.title : productTitle;
          messages.push({
            html:
              "You can only order <b>" +
              vLimit +
              "</b> of “" +
              label +
              "” per order. Please remove <b>" +
              excess +
              "</b> to continue.",
            nodes: v.nodes,
          });
        }
      });

      // ---- Layer 3: product cap (only if no variant cap already flagged) -----
      if (!variantViolation) {
        const pLimit = parseInt(productLimits[pid], 10);
        if (pLimit && pLimit > 0 && p.total > pLimit) {
          const excess = p.total - pLimit;
          messages.push({
            html:
              "Only <b>" +
              pLimit +
              "</b> of “" +
              productTitle +
              "” can be purchased per order. Please remove <b>" +
              excess +
              "</b> to continue.",
            nodes: p.nodes,
          });
        }
      }
    }

    if (messages.length) {
      hasViolation = true;
      messages.forEach((m) => {
        m.nodes.forEach((el) => el.classList.add("cart-item--limit-exceeded"));
        firstNode.parentNode.insertBefore(buildMsg(m.html), firstNode);
      });
    }
  });

  if (checkoutBtn) {
    if (hasViolation) {
      checkoutBtn.classList.add("cart__checkout-button--disabled");
      checkoutBtn.setAttribute("aria-disabled", "true");
      checkoutBtn.dataset.blocked = "true";
    } else {
      checkoutBtn.classList.remove("cart__checkout-button--disabled");
      checkoutBtn.removeAttribute("aria-disabled");
      delete checkoutBtn.dataset.blocked;
    }
  }
}

// ---- 1. Variant swap inside the side cart ---------------------------------
// Swaps the line item to the newly-selected variant. Because /cart/change.js
// cannot change a variant, we remove the old line and add the new variant,
// preserving quantity and (where still offered) the subscription cadence.
async function handleCartVariantChange(select) {
  const cartItem = select.closest(".cart-item");
  const lineItemKey = select.dataset.lineItemKey;
  const newVariantId = parseInt(select.value, 10);
  if (!lineItemKey || !newVariantId) return;

  // Plans available on the newly-selected variant (from the <option>).
  const selectedOption = select.options[select.selectedIndex];
  const newPlanIds = (selectedOption && selectedOption.dataset.planIds
    ? selectedOption.dataset.planIds
    : ""
  )
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  select.disabled = true;
  if (cartItem) cartItem.classList.add("cart-item--updating");

  try {
    const cart = await fetch("/cart.js").then((r) => r.json());
    const line = cart.items.find((i) => i.key === lineItemKey);
    if (!line) throw new Error("Line item not found for variant swap");

    if (line.variant_id === newVariantId) {
      select.disabled = false;
      if (cartItem) cartItem.classList.remove("cart-item--updating");
      return;
    }

    const quantity = line.quantity || 1;
    const currentPlanId = line.selling_plan_allocation
      ? String(line.selling_plan_allocation.selling_plan.id)
      : null;

    // Decide which selling plan (if any) the new variant should keep.
    let sellingPlan = null;
    if (currentPlanId && newPlanIds.includes(currentPlanId)) {
      sellingPlan = currentPlanId; // same cadence still offered
    } else if (currentPlanId && newPlanIds.length) {
      sellingPlan = newPlanIds[0]; // fall back to the new variant's first plan
    } // else: one-time purchase

    const addBody = { id: newVariantId, quantity };
    if (line.properties && Object.keys(line.properties).length) {
      addBody.properties = line.properties; // carry line properties across
    }
    if (sellingPlan) addBody.selling_plan = parseInt(sellingPlan, 10);

    // Remove the old line, then add the new variant.
    await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: lineItemKey, quantity: 0 }),
    });

    const addRes = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(addBody),
    });

    if (!addRes.ok) {
      // Swap failed — put the original variant back so nothing is lost.
      const revert = { id: line.variant_id, quantity };
      if (line.properties && Object.keys(line.properties).length) {
        revert.properties = line.properties;
      }
      if (currentPlanId) revert.selling_plan = parseInt(currentPlanId, 10);
      await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(revert),
      });
    }

    const updated = await fetch("/cart.js").then((r) => r.json());
    updateBubbleCartCount(updated.item_count);
    await updateCartDrawer();
    setTimeout(() => {
      try { FGSECOMAPP.renderPromotionMessage(); } catch (e) {}
      document.dispatchEvent(new CustomEvent("bogos:gifts"));
    }, 1000);
  } catch (err) {
    console.error("Cart variant change failed:", err);
    await updateCartDrawer();
  }
}

// --- Sidecart order-confidence-strip A/B test tracking ---------------------
function getSidecartTestContext() {
  const flags = window.pdmFeatureFlags;
  const isHoldout = flags && flags.isUserExcludedByHoldout ? flags.isUserExcludedByHoldout() : false;
  const bucket = isHoldout ? "holdout" : "exposed";
  const variant = isHoldout
    ? "holdout"
    : document.body.classList.contains("exp-test-cart-order-confidence-strip")
      ? "treatment"
      : "control";
  return { variant, bucket };
}

function trackSidecartEvent(eventName, extraProps = {}) {
  const { variant, bucket } = getSidecartTestContext();
  const payload = { variant, bucket, ...extraProps };

  if (typeof gtag === "function") {
    // send_to forced: this site's gtag default routes bare gtag('event', ...)
    // calls to the Google Ads destination (AW-10986990578) only — verified via
    // network inspection these events never reached analytics.google.com/g/collect
    // without this. GA4 measurement ID confirmed present in this container.
    gtag('event', eventName, { ...payload, send_to: 'G-VYKNHEY3EE' });
  }
}

// Both elements sit always-visible in the drawer (strip below header, trust
// bar pinned in the footer) — opening the drawer while in treatment is enough
// to count them as viewed, no IntersectionObserver needed.
function trackSidecartTreatmentViews() {
  if (getSidecartTestContext().variant !== "treatment") return;
  trackSidecartEvent("order_confidence_strip_viewed");
  trackSidecartEvent("value_props_viewed");
}
// -----------------------------------------------------------------------------

function formatMoney(cents, format) {
  if (typeof cents == "string") {
    cents = cents.replace(".", "");
  }
  var value = "";
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = format || this.money_format;

  function defaultOption(opt, def) {
    return typeof opt == "undefined" ? def : opt;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ",");
    decimal = defaultOption(decimal, ".");

    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    var parts = number.split("."),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands),
      cents = parts[1] ? decimal + parts[1] : "";

    return dollars + cents;
  }

  var match = formatString.match(placeholderRegex);

  if (match) {
    switch (match[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
    }
  }

  return formatString.replace(placeholderRegex, value);
}

const setActiveElement = (element) => {
  activeElement = element;
};

const openCartDrawer = (triggeredBy) => {
  if (triggeredBy) setActiveElement(triggeredBy);

  // Add-to-cart finished — remove any button spinners.
  clearAtcSpinners();

  trackSidecartEvent("sidecart_opened");
  trackSidecartTreatmentViews();

  const cartDrawerNote = cartDrawer.querySelector('[id^="Details-"] summary');
  if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) {
    setSummaryAccessibility(cartDrawerNote);
  }

  setTimeout(() => {
    cartDrawer.classList.add("animate", "active");
  });

  const recartPopupRoot = document.querySelector("#recart-popup-root");
  if (recartPopupRoot) {
    // recartPopupRoot.style.display = "none";
    recartPopupRoot.style.visibility = "hidden";
  }
  updateCartDrawer();
  const html = document.querySelector("html");
  html.style.overflow = "hidden";
};

const closeCartDrawer = () => {
  cartDrawer.classList.add("closing");
  const html = document.querySelector("html");
  document.querySelector("html").classList.remove("cart-drawer-active");
  html.style.overflow = "auto";
  const recartPopupRoot = document.querySelector("#recart-popup-root");
  if (recartPopupRoot) {
    // recartPopupRoot.style.display = "block";
    recartPopupRoot.style.visibility = "visible";
  }

  setTimeout(() => {
    cartDrawer.classList.remove("active", "closing");
  }, 300);
};

const setSummaryAccessibility = (cartDrawerNote) => {
  cartDrawerNote.setAttribute("role", "button");
  cartDrawerNote.setAttribute("aria-expanded", "false");

  const nextElementId = cartDrawerNote.nextElementSibling?.getAttribute("id");
  if (nextElementId) {
    cartDrawerNote.setAttribute("aria-controls", nextElementId);
  }

  cartDrawerNote.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open")
    );
  });

  cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
};

const setHeaderCartIconAccessibility = () => {
  const cartLink = document.querySelector("#cart-drawer-trigger");
  if (cartLink) {
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");

    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      openCartDrawer(cartLink);
    });

    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        openCartDrawer(cartLink);
      }
    });
  }
};

// ============================================================
// Remove Shipping Protection if it somehow ends up in cart
// (safety net — SP is no longer added at any point in the UI)
// ============================================================

async function removeOrphanedShippingProtection() {
  try {
    const cart = await fetch('/cart.js').then(r => r.json());
    const spItem = cart.items.find(item => item.product_id === 8957611540696);
    if (!spItem) return;
    const otherItems = cart.items.filter(item => item.product_id !== 8957611540696);
    if (otherItems.length === 0) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: spItem.key, quantity: 0 }),
      });
    }
  } catch (err) {
    console.error('removeOrphanedShippingProtection error:', err);
  }
}

// ============================================================
// Dedup: only merge line items that are exact duplicates —
// same variant ID AND same selling plan ID (or both one-time).
// Different plans (30-day vs 60-day) and one-time vs sub
// are intentionally kept as separate line items.
// ============================================================

async function deduplicateCartLineItems() {
  try {
    const cart = await fetch('/cart.js').then(r => r.json());

    // Build a composite key: variantId + sellingPlanId (or 'onetime')
    const groupMap = {};
    for (const item of cart.items) {
      const planId = item.selling_plan_allocation
        ? String(item.selling_plan_allocation.selling_plan.id)
        : 'onetime';
      const compositeKey = `${item.variant_id}__${planId}`;
      if (!groupMap[compositeKey]) groupMap[compositeKey] = [];
      groupMap[compositeKey].push(item);
    }

    const updates = {};
    let hasDupes = false;

    for (const [key, items] of Object.entries(groupMap)) {
      if (items.length < 2) continue;
      hasDupes = true;

      // Keep the first, sum quantities, zero out the rest
      const keepItem = items[0];
      const totalQty = items.reduce((sum, i) => sum + i.quantity, 0);
      updates[keepItem.key] = totalQty;
      for (let i = 1; i < items.length; i++) {
        updates[items[i].key] = 0;
      }
    }

    if (!hasDupes) return false;

    // Single batched update — avoids race conditions
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });

    return true;
  } catch (err) {
    console.error('deduplicateCartLineItems error:', err);
    return false;
  }
}

// ============================================================
// Free Gift With Purchase (July Fruit Sale)
// ------------------------------------------------------------
// Config comes from window.TWC_FGWP (rendered by sidebar-cart.liquid
// from the theme settings):
//   { active, variantId, productId, label, spId }
//
// Rules:
//   - If the cart has >= 1 paid item and no gift  -> add the gift (qty 1)
//   - If the cart has 0 paid items and a gift     -> remove the gift
//     (guarantees you can never check out with the gift alone)
//   - Gift quantity is always forced to 1
//   - Shipping Protection (spId) does NOT count as a paid item
//
// A single in-flight promise prevents duplicate gift lines when
// updateCartDrawer() fires multiple times in quick succession.
// ============================================================

let fgwpPromise = null;
// True when the cart qualifies for a gift, more than one gift is configured,
// and the customer hasn't chosen one yet -> we should show the picker popup.
let fgwpShouldPrompt = false;
// True once the customer closes the picker without choosing (so we don't
// keep auto-reopening it on every cart refresh). Reset when they qualify afresh
// or choose a gift. The "Choose your gift" banner remains available in the cart.
let fgwpPickerDismissed = false;

// The configured gift variant ids (from window.TWC_FGWP.gifts).
function fgwpGiftVariantIds(cfg) {
  if (!cfg || !Array.isArray(cfg.gifts)) return [];
  return cfg.gifts.map((g) => g.variantId).filter(Boolean);
}

function fgwpIsGiftLine(item, cfg) {
  if (item.properties && item.properties._free_gift) return true;
  const ids = fgwpGiftVariantIds(cfg);
  if (ids.includes(item.variant_id)) return true;
  return false;
}

function fgwpDrawerIsOpen() {
  const d = document.querySelector('cart-drawer');
  return !!(d && d.classList.contains('active'));
}

function openGiftPicker() {
  const p = document.getElementById('fgwp-gift-picker');
  if (!p) return;
  p.classList.add('is-open');
  p.setAttribute('aria-hidden', 'false');
}

function closeGiftPicker() {
  const p = document.getElementById('fgwp-gift-picker');
  if (!p) return;
  p.classList.remove('is-open');
  p.setAttribute('aria-hidden', 'true');
}

// Add a specific gift variant as THE free gift (removes any existing gift first).
async function fgwpAddGift(variantId) {
  const cfg = window.TWC_FGWP || {};
  try {
    const cart = await fetch('/cart.js').then((r) => r.json());
    const giftLines = cart.items.filter((item) => fgwpIsGiftLine(item, cfg));
    if (giftLines.length) {
      const updates = {};
      giftLines.forEach((g) => { updates[g.key] = 0; });
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
    }
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          { id: variantId, quantity: 1, properties: { _free_gift: cfg.label || 'Free Gift' } },
        ],
      }),
    });
    fgwpPickerDismissed = false;
  } catch (err) {
    console.error('fgwpAddGift error:', err);
  }
}

async function handleFreeGiftWithPurchase(collectionOk = true) {
  // Coalesce concurrent calls onto one operation.
  if (fgwpPromise) {
    try { await fgwpPromise; } catch (e) {}
    return false;
  }

  fgwpPromise = (async () => {
    let changed = false;
    const cfg = window.TWC_FGWP;
    if (!cfg) return changed;

    const gifts = Array.isArray(cfg.gifts) ? cfg.gifts : [];

    try {
      const cart = await fetch('/cart.js').then((r) => r.json());

      const giftLines = cart.items.filter((item) => fgwpIsGiftLine(item, cfg));
      const paidItems = cart.items.filter(
        (item) => !fgwpIsGiftLine(item, cfg) && item.product_id !== cfg.spId
      );

      const removeGiftLines = async () => {
        if (!giftLines.length) return false;
        const updates = {};
        giftLines.forEach((g) => { updates[g.key] = 0; });
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates }),
        });
        return true;
      };

      // Feature disabled or no gift configured -> clean up any stale gift.
      if (!cfg.active || gifts.length === 0) {
        if (cfg.active && gifts.length === 0) {
          console.warn(
            '[FGWP] Enabled but no gift variant could be resolved. Check that at ' +
            'least one gift product is set in theme settings, is Active (not Draft), ' +
            'and has a variant. window.TWC_FGWP =', cfg
          );
        }
        fgwpShouldPrompt = false;
        changed = await removeGiftLines();
        return changed;
      }

      // Not eligible -> never keep the gift, and reset the dismissed flag so the
      // customer is prompted again next time they qualify. Covers:
      //  - cart would be gift-only (or empty), or
      //  - cart has no item from the eligible collections (collectionOk === false).
      if (paidItems.length === 0 || !collectionOk) {
        fgwpShouldPrompt = false;
        fgwpPickerDismissed = false;
        changed = await removeGiftLines();
        return changed;
      }

      // There is at least one paid item.
      if (giftLines.length > 0) {
        // A gift is already chosen -> collapse duplicates and force quantity to 1.
        fgwpShouldPrompt = false;
        const keep = giftLines[0];
        const updates = {};
        if (keep.quantity !== 1) updates[keep.key] = 1;
        for (let i = 1; i < giftLines.length; i++) updates[giftLines[i].key] = 0;
        if (Object.keys(updates).length) {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates }),
          });
          changed = true;
        }
      } else if (gifts.length === 1) {
        // Exactly one gift configured -> add it automatically (original behaviour).
        fgwpShouldPrompt = false;
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: gifts[0].variantId,
                quantity: 1,
                properties: { _free_gift: cfg.label || 'Free Gift' },
              },
            ],
          }),
        });
        changed = true;
      } else {
        // Multiple gifts configured, none chosen yet -> prompt the customer to pick.
        // The picker is opened by updateCartDrawer() once the drawer is on screen.
        fgwpShouldPrompt = true;
      }
    } catch (err) {
      console.error('handleFreeGiftWithPurchase error:', err);
    }
    return changed;
  })();

  try {
    return await fgwpPromise;
  } finally {
    fgwpPromise = null;
  }
}

// ---- Free-gift picker: delegated events (registered once on script load) ----
document.addEventListener('click', async (e) => {
  // Selecting a gift from the popup.
  const opt = e.target.closest('.fgwp-option');
  if (opt) {
    e.preventDefault();
    const vid = parseInt(opt.getAttribute('data-fgwp-variant'), 10);
    if (!vid) return;
    opt.setAttribute('aria-busy', 'true');
    await fgwpAddGift(vid);
    closeGiftPicker();
    await updateCartDrawer();
    openCartDrawer();
    opt.removeAttribute('aria-busy');
    return;
  }

  // Opening the popup (banner or "Change gift" link).
  // Close the cart drawer first so the popup gets a clean, focused view
  // (it's reopened automatically once a gift is chosen).
  if (e.target.closest('[data-fgwp-open]')) {
    e.preventDefault();
    fgwpPickerDismissed = false;
    if (typeof closeCartDrawer === 'function') closeCartDrawer();
    openGiftPicker();
    return;
  }

  // Closing the popup (X button or overlay).
  if (e.target.closest('[data-fgwp-close]')) {
    e.preventDefault();
    fgwpPickerDismissed = true;
    closeGiftPicker();
    return;
  }
});

document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  const p = document.getElementById('fgwp-gift-picker');
  if (p && p.classList.contains('is-open')) {
    fgwpPickerDismissed = true;
    closeGiftPicker();
  }
});

// ---- Short subscription-frequency labels -----------------------------------
// Recharge plan names can be long ("Delivery every 45 days with 10% discount").
// Rewrite each frequency <option> to a compact "Every N days" (keeping the full
// text as a tooltip) so the dropdown stays on the right and matches the design.
// The <option> value (selling plan id) is untouched, so subscribing still works.
function twcShortenPlanLabels(root) {
  const scope = root || document;
  scope
    .querySelectorAll(".cart-sub__freq .selling-plan-select option")
    .forEach((opt) => {
      if (opt.dataset.twcShortened) return;
      const m = opt.textContent.match(/(\d+)\s*days?/i);
      if (m) {
        const original = opt.textContent.trim();
        opt.title = original;
        opt.textContent = "Every " + m[1] + " days";
        opt.dataset.twcShortened = "1";
      }
    });
}

// Fetch the cart-drawer section HTML into a detached container we can read.
async function fgwpFetchDrawer() {
  const res = await fetch(`${window.Shopify.routes.root}?section_id=cart-drawer`);
  const text = await res.text();
  const html = document.createElement("div");
  html.innerHTML = text;
  return html;
}

// Read the Liquid-computed eligible-collections flag from a fetched drawer.
// Defaults to true (don't block gifts) if the marker is absent.
function fgwpReadCollectionOk(html) {
  const el = html && html.querySelector('#fgwp-cart-state');
  return !el || el.dataset.collectionOk !== 'false';
}

async function updateCartDrawer() {
  await removeOrphanedShippingProtection();
  await deduplicateCartLineItems();

  // Render once to learn whether the cart qualifies by eligible collections
  // (computed in Liquid, which can see each item's collections), then reconcile
  // the gift against that. Only re-render if the gift actually changed.
  let html = await fgwpFetchDrawer();
  const collectionOk = fgwpReadCollectionOk(html);
  const giftChanged = await handleFreeGiftWithPurchase(collectionOk);
  if (giftChanged) {
    html = await fgwpFetchDrawer();
  }

  const newBox = html.querySelector("cart-drawer").innerHTML;
  document.querySelector("cart-drawer").innerHTML = newBox;
  addCartDrawerListeners();

  // Re-check per-order quantity limits and enable/disable checkout accordingly.
  enforceCartQuantityLimits();
  twcShortenPlanLabels();

  // Prompt the customer to choose their free gift (only when 2-3 gifts are set,
  // the cart qualifies, the drawer is open, and they haven't dismissed it).
  if (fgwpShouldPrompt && fgwpDrawerIsOpen() && !fgwpPickerDismissed) {
    openGiftPicker();
  }

  setTimeout(() => {
    FGSECOMAPP.renderPromotionMessage();
    document.dispatchEvent(new CustomEvent('bogos:gifts'));
  }, 100);
}

async function addToCartSubscription(form) {
  fetch("/cart/add.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify(form),
  })
    .then((response) => {
      if (!response.ok) throw new Error("Network response was not ok");
      return response.json();
    })
    .then((data) => {
      updateCartDrawer();
      openCartDrawer();
      updateBubbleCartCount(data.item_count);
    })
    .catch((error) => {
      console.error("Error:", error);
    });
}

const updateBubbleCartCount = (count) => {
  if (count === undefined) return;
  const cartCount = document.querySelector("#CartCount");
  if (cartCount) cartCount.innerHTML = count;
};

async function handleProductFormSubmit(e) {
  let form;
  if (e instanceof Event) {
    e.preventDefault();
    form = e.currentTarget;
  } else {
    form = e;
  }
  const formData = new FormData(form);
  const button = e.submitter;
  button?.classList.add("loading");
  button?.setAttribute("aria-disabled", "true");

  const data = {};
  for (const [key, value] of formData.entries()) {
    data[key] = value;
  }

  // ---- 3. PDP add-to-cart guard (exclusivity + variant cap + product cap) --
  // Layered against the currently selected variant:
  //   1. variant exclusivity  (data-variant-exclusive) — only one option at a time
  //   2. per-variant cap       (data-variant-max[selectedVariantId])
  //   3. product cap           (data-max-quantity)
  // Reads live cart state so it reflects one-time + subscription lines together.
  const guardProductId = form.getAttribute("data-productid");
  const selectedVariantId = String(data.id || "");
  const productCap = parseInt(form.getAttribute("data-max-quantity"), 10);
  const isExclusive = form.getAttribute("data-variant-exclusive") === "true";
  let variantMaxMap = {};
  let variantTitleMap = {};
  try { variantMaxMap = JSON.parse(form.getAttribute("data-variant-max") || "{}"); } catch (e) {}
  try { variantTitleMap = JSON.parse(form.getAttribute("data-variant-titles") || "{}"); } catch (e) {}
  const variantCap = parseInt(variantMaxMap[selectedVariantId], 10);
  const selectedVariantTitle = variantTitleMap[selectedVariantId] || "this option";

  // Clear any previous inline error so each attempt is re-evaluated cleanly.
  clearPdpInlineError(form);

  if (guardProductId && (isExclusive || (variantCap > 0) || (productCap > 0))) {
    try {
      const currentCart = await fetch("/cart.js").then((r) => r.json());
      const productItems = currentCart.items.filter(
        (i) => String(i.product_id) === String(guardProductId)
      );
      const totalInCart = productItems.reduce((s, i) => s + i.quantity, 0);
      const sameVariantQty = productItems
        .filter((i) => String(i.id) === selectedVariantId)
        .reduce((s, i) => s + i.quantity, 0);
      const otherVariantItems = productItems.filter(
        (i) => String(i.id) !== selectedVariantId
      );
      const addQty = parseInt(data.quantity, 10) || 1;

      const blockAdd = (message) => {
        showPdpInlineError(form, button, message);
        button?.classList.remove("loading");
        button?.removeAttribute("aria-disabled");
        clearAtcSpinners();
      };

      // Layer 1 — exclusivity: a different variant is already in the cart.
      if (isExclusive && otherVariantItems.length > 0) {
        const otherTitle = otherVariantItems[0].variant_title || "another option";
        blockAdd(
          'You already have "' +
            otherTitle +
            '" in your cart. Only one option of this product can be added at a time — remove it first to switch to "' +
            selectedVariantTitle +
            '".'
        );
        return;
      }

      // Layer 2 — per-variant cap for the selected variant.
      if (variantCap > 0 && sameVariantQty + addQty > variantCap) {
        const remaining = Math.max(0, variantCap - sameVariantQty);
        let msg;
        if (sameVariantQty > 0 && remaining > 0) {
          msg = 'You can add ' + remaining + ' more of "' + selectedVariantTitle +
                '" — the limit is ' + variantCap + ' per order.';
        } else {
          msg = 'You can only buy ' + variantCap + ' of "' + selectedVariantTitle +
                '" — the limit is ' + variantCap + ' per order.';
        }
        blockAdd(msg);
        return;
      }

      // Layer 3 — overall product cap.
      if (productCap > 0 && totalInCart + addQty > productCap) {
        const remaining = Math.max(0, productCap - totalInCart);
        let msg;
        if (totalInCart > 0 && remaining > 0) {
          msg = "You can add " + remaining + " more of this product — the limit is " +
                productCap + " per order.";
        } else {
          msg = "You can only buy " + productCap +
                " of this product — the limit is " + productCap + " per order.";
        }
        blockAdd(msg);
        return;
      }
    } catch (err) {
      console.error("Max-qty PDP guard error:", err);
      // On error, fall through and let the normal add proceed.
    }
  }

  const toggle = form.querySelector('#subToggle');
  if (toggle && toggle.checked) {
    const customDropdown = form.querySelector('#custom-subscription-dropdown');
    if (customDropdown && customDropdown.value) {
      data['selling_plan'] = customDropdown.value;
    }
  } else {
    if (toggle) delete data['selling_plan'];
  }

  try {
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Error adding to cart:', errorData);
      throw new Error('Error adding to cart.');
    }

    const res = await fetch("/cart.js");
    const cart = await res.json();

    await updateCartDrawer();
    updateBubbleCartCount(cart.item_count);
    openCartDrawer();
    setTimeout(() => {
      FGSECOMAPP.renderPromotionMessage();
      document.dispatchEvent(new CustomEvent('bogos:gifts'));
    }, 1000);

  } catch (error) {
    console.error("Error in request:", error);
  } finally {
    button?.classList.remove("loading");
    button?.removeAttribute("aria-disabled");
    clearAtcSpinners();
  }
}

// ============================================================
// FBT Swiper — ensures both CSS and JS are loaded before init
// ============================================================

function loadSwiperCSS() {
  return new Promise((resolve) => {
    const existing = document.querySelector('link[href*="swiper-bundle.min.css"]');
    if (existing) {
      if (existing.sheet) {
        resolve();
      } else {
        existing.addEventListener('load', resolve);
        existing.addEventListener('error', resolve);
      }
      return;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
    link.addEventListener('load', resolve);
    link.addEventListener('error', resolve);
    document.head.appendChild(link);
  });
}

function loadSwiperScript() {
  return new Promise((resolve) => {
    if (window.Swiper) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
    script.onload = resolve;
    script.onerror = resolve;
    document.head.appendChild(script);
  });
}

async function initFbtSwiper() {
  const el = document.querySelector('.js-fbt-swiper');
  if (!el) return;

  await Promise.all([loadSwiperCSS(), loadSwiperScript()]);

  if (el.swiper) el.swiper.destroy(true, true);

  const section = el.closest('.fbt-section');
  const prevBtn = section?.querySelector('.fbt-nav-bar__prev');
  const nextBtn = section?.querySelector('.fbt-nav-bar__next');
  const paginationEl = section?.querySelector('.fbt-swiper__pagination');

  new Swiper(el, {
    slidesPerView: 1,
    spaceBetween: 8,
    loop: false,
    pagination: {
      el: paginationEl,
      clickable: true,
    },
    navigation: {
      prevEl: prevBtn,
      nextEl: nextBtn,
    },
  });
}

// ============================================================
// Tooltip positioning — keeps bubble within viewport
// ============================================================

function setupTooltipPositioning() {
  const margin = 16;
  const gap = 8;

  document.addEventListener('mouseover', (e) => {
    const tooltip = e.target.closest('.cart-protection-tooltip');
    if (!tooltip) return;

    const bubble = tooltip.querySelector('.cart-protection-tooltip__text');
    if (!bubble) return;

    bubble.style.top = '-9999px';
    bubble.style.left = '-9999px';
    bubble.classList.add('is-visible');

    requestAnimationFrame(() => {
      const iconRect = tooltip.getBoundingClientRect();
      const bubbleWidth = bubble.offsetWidth;
      const bubbleHeight = bubble.offsetHeight;

      let top = iconRect.top - bubbleHeight - gap;

      if (top < margin) {
        top = iconRect.bottom + gap;
        bubble.classList.add('tooltip--below');
      } else {
        bubble.classList.remove('tooltip--below');
      }

      let left = iconRect.left + (iconRect.width / 2) - (bubbleWidth / 2);
      left = Math.max(margin, Math.min(left, window.innerWidth - bubbleWidth - margin));

      bubble.style.top = `${top}px`;
      bubble.style.left = `${left}px`;
    });
  });

  document.addEventListener('mouseout', (e) => {
    const tooltip = e.target.closest('.cart-protection-tooltip');
    if (!tooltip) return;
    const bubble = tooltip.querySelector('.cart-protection-tooltip__text');
    if (!bubble) return;
    bubble.classList.remove('is-visible', 'tooltip--below');
  });
}

function setupDynamicListeners() {
  if (!cartDrawer) return;

  cartDrawer.addEventListener("click", async (event) => {

    // Prevent checkout while a per-order quantity limit is exceeded.
    const blockedCheckout = event.target.closest("#CartDrawer-Checkout");
    if (blockedCheckout && blockedCheckout.dataset.blocked === "true") {
      event.preventDefault();
      const firstError = cartDrawer.querySelector(".cart-item--limit-exceeded");
      if (firstError) firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const overlayElement = event.target.closest("#CartDrawer-Overlay");
    if (overlayElement) {
      closeCartDrawer();
      return;
    }

    const closeButton = event.target.closest(".drawer__close");
    if (closeButton) {
      closeCartDrawer();
      return;
    }

    const button =
      event.target.closest(".quantity__button") ||
      event.target.closest("cart-remove-button");

    if (!button) return;

    event.preventDefault();

    const rootItem = button.closest("[data-line-item-key]");
    const key = rootItem ? rootItem.getAttribute("data-line-item-key") : null;
    if (!key) return;

    let newQuantity = 0;
    let fetchBody = {};

    if (button.classList.contains("quantity__button")) {
      const quantityInput = button.parentElement.querySelector("input");
      const currentQuantity = Number(quantityInput.value);
      newQuantity = button.name === "plus" ? currentQuantity + 1 : currentQuantity - 1;
      fetchBody = { updates: { [key]: newQuantity } };
    } else if (button.tagName === "CART-REMOVE-BUTTON") {
      newQuantity = 0;
      fetchBody = { updates: { [key]: 0 } };
    } else {
      return;
    }

    if (newQuantity >= 0) {
      // Small spinner on the line while the cart updates + drawer re-renders,
      // so the customer sees it's working (and any limit error appears after).
      rootItem.classList.add("cart-item--updating");
      const res = await fetch("/cart/update.js", {
        method: "post",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(fetchBody),
      });

      const cart = await res.json();
      updateCartDrawer();
      updateBubbleCartCount(cart.item_count);
      setTimeout(() => {
        FGSECOMAPP.renderPromotionMessage();
        document.dispatchEvent(new CustomEvent('bogos:gifts'));
      }, 1000);
    }
  });

  cartDrawer.addEventListener("change", async (event) => {
    // Variant selector — swap the line item to the chosen variant.
    const variantSelect = event.target.closest(".cart-item__variant-select");
    if (variantSelect) {
      await handleCartVariantChange(variantSelect);
      return;
    }

    const checkbox = event.target.closest(".input-selling");
    const select = event.target.closest(".selling-plan-select");

    if (checkbox) {
      const lineItemKey = checkbox.dataset.lineItemKey;
      const sellingPlanId = checkbox.checked ? checkbox.dataset.sellingPlanId : null;
      const cartItem = checkbox.closest(".cart-item");
      const quantityInput = cartItem?.querySelector(".quantity__input");
      const currentQuantity = quantityInput?.value || 1;
      const selectWrapper = cartItem?.querySelector(".selling-plan-select-wrapper");
      const planSelect = cartItem?.querySelector(".selling-plan-select");

      if (selectWrapper && planSelect) {
        if (checkbox.checked) {
          selectWrapper.classList.remove("subscription-disabled");
          planSelect.disabled = false;
        } else {
          selectWrapper.classList.add("subscription-disabled");
          planSelect.disabled = true;
        }
      }

      checkbox.disabled = true;
      const subBlockCb = cartItem?.querySelector(".cart-sub");
      subBlockCb?.classList.add("cart-sub--loading");

      try {
        const response = await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: lineItemKey,
            selling_plan: sellingPlanId,
            quantity: currentQuantity,
          }),
        });
        if (!response.ok) throw new Error("Error updating cart");
        await response.json();
        updateCartDrawer();
      } catch (error) {
        console.error("Failed to update subscription:", error);
        checkbox.checked = !checkbox.checked;
        if (selectWrapper && planSelect) {
          if (checkbox.checked) {
            selectWrapper.classList.remove("subscription-disabled");
            planSelect.disabled = false;
          } else {
            selectWrapper.classList.add("subscription-disabled");
            planSelect.disabled = true;
          }
        }
        await updateCartDrawer();
      } finally {
        checkbox.disabled = false;
      }

    } else if (select) {
      const sellingPlanId = select.value || null;
      const lineItemKey = select.dataset.lineItemKey;
      const cartItem = select.closest(".cart-item");
      const quantityInput = cartItem?.querySelector(".quantity__input");
      const currentQuantity = quantityInput?.value || 1;

      select.disabled = true;
      const subBlockSel = cartItem?.querySelector(".cart-sub");
      subBlockSel?.classList.add("cart-sub--loading");

      try {
        const response = await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: lineItemKey,
            selling_plan: sellingPlanId,
            quantity: currentQuantity,
          }),
        });
        if (!response.ok) throw new Error("Error updating cart with selling plan");
        await response.json();

        const checkboxEl = cartItem?.querySelector(".input-selling");
        if (checkboxEl) checkboxEl.checked = !!sellingPlanId;

        await updateCartDrawer();
        setTimeout(() => {
          FGSECOMAPP.renderPromotionMessage();
          document.dispatchEvent(new CustomEvent('bogos:gifts'));
        }, 1000);
      } catch (error) {
        console.error("Failed to update selling plan:", error);
      } finally {
        select.disabled = false;
      }
    }
  });

  cartDrawer.addEventListener("submit", (event) => {
    const fbtForm = event.target.closest(".fbt-form .form-product");
    if (fbtForm) {
      event.preventDefault();
      handleProductFormSubmit(fbtForm);
    }
  });

  cartDrawer.addEventListener(
    "keyup",
    (evt) => evt.code === "Escape" && closeCartDrawer()
  );
}

function setupStaticListeners() {
  if (!cartDrawer) return;

  document.querySelectorAll(".group-product__form").forEach((form) => {
    form.addEventListener("submit", handleProductFormSubmit);
  });

  document.querySelectorAll(".group-product__form2").forEach((form) => {
    form.addEventListener("submit", handleProductFormSubmit);
  });
  document.querySelectorAll(".rca-add-to-cart-submit").forEach((form) => {
    form.addEventListener("submit", handleProductFormSubmit);
  });

  document.querySelectorAll(".shg-btn").forEach((button) => {
    button.addEventListener("click", (event) => {
      const href = button.getAttribute("href");
      const idMatch = href.match(/id=(\d+)/);
      const sellingPlanMatch = href.match(/selling_plan=(\d+)/);
      if (!idMatch || !sellingPlanMatch) return;

      event.preventDefault();

      const payload = {
        items: [{
          id: parseInt(idMatch[1], 10),
          quantity: 1,
          selling_plan: parseInt(sellingPlanMatch[1], 10),
        }],
      };
      addToCartSubscription(payload);
    });
  });

  document.addEventListener("ssp:cart:update", () => {
    updateCartDrawer();
    setTimeout(() => {
      console.log("Retrying cart update after 0.5s");
      updateCartDrawer();
    }, 500);
  });

  document.addEventListener("shopify:cart:updated", () => {
    updateCartDrawer();
  });

  setHeaderCartIconAccessibility();
}

async function initCart() {
  const response = await fetch('/cart.js');
  const cart = await response.json();
  updateBubbleCartCount(cart.item_count);
  setTimeout(() => {
    updateCartDrawer();
  }, 500);
}

async function addCartDrawerListeners() {
  initFbtSwiper();
}

window.addEventListener("pageshow", async function (event) {
  if (event.persisted) {
    // Coming back from checkout — deduplicate first, then sync cart
    await deduplicateCartLineItems();
    initCart();
  }
});

document.addEventListener("DOMContentLoaded", async () => {
  // Block checkout whenever a cart limit error is showing (robust, always on).
  twcGuardCheckout();
  // Universal add-to-cart spinner (registered once, works everywhere).
  setupAtcSpinner();
  // Deduplicate on page load in case checkout created dupes
  await deduplicateCartLineItems();
  setupStaticListeners();
  setupDynamicListeners();
  addCartDrawerListeners();
  // Enforce limits on the server-rendered drawer, then keep it in sync via initCart.
  enforceCartQuantityLimits();
  twcShortenPlanLabels();
  initCart();
});